/* eslint-disable camelcase */
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Download } from 'lucide-react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import DocumentIcon from './icons/DocumentIcon'
import MicrophoneIcon from './icons/MicrophoneIcon'
import TeamIcon from './icons/TeamIcon'
import LeaderboardSection from './LeaderboardSection'
import {
  calculateTrend,
  calculateTrendGeneric,
  computeAllPossibleDays,
  computeAllPossibleWeeks,
  computeDailyWordCounts,
  computeWeeklyWordCounts,
  exportUsageCSV,
  formatDayToDate,
  formatLastUsed,
  formatWeekToDate,
  getDateRange,
  getDateRangeGeneric,
} from './usageDashboardUtils'

import styles from './styles.module.scss'

import { getEnterpriseUsage } from '@/api/usage'
import Button from '@/components/Button'
import Tabs from '@/components/Tabs'
import { useAnalytics } from '@/hooks/useAnalytics'
import { usePricing } from '@/hooks/usePricing'
import {
  selectUsageIsLoaded,
  selectUsageLastFetchedAt,
  selectUsageUsers,
  setUsageData,
} from '@/store/features/usage/usageSlice'
import {
  selectCurrentEnterprise,
  selectHasActiveOrTrialingEnterpriseSubscription,
  selectIsAdmin,
  selectName,
  selectPlanType,
  selectUser,
} from '@/store/features/user/userSlice'
import { PlanType, SettingType } from '@/types'
import { EventName } from '@/utils/eventDefinitions'

type SortField = 'name' | 'words' | 'lastUsed'
type SortDirection = 'asc' | 'desc'
type ViewMode = 'daily' | 'weekly'

type ChartDataPoint = {
  month: string
  words: number
}

type TeammateRow = {
  userId: string
  name: string
  email: string
  words: number
  lastUsed: string
  lastUsedTimestamp: number
  isYou: boolean
  avatar: string
  color: string
}

type TrendData = {
  percentage: number
  direction: 'up' | 'down' | 'neutral'
  isPositive: boolean
}

const UsagePage = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [selectedUserId, setSelectedUserId] = useState<string>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [isExporting, setIsExporting] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('weekly')
  const [weekOffset, setWeekOffset] = useState(0) // 0 = most recent 10 weeks
  const [dayOffset, setDayOffset] = useState(0) // 0 = most recent 14 days
  const [activeTab, setActiveTab] = useState(0) // 0 = Overview, 1 = Leaderboard
  const userName = useSelector(selectName)
  const currentUser = useSelector(selectUser)
  const isAdmin = useSelector(selectIsAdmin)
  const planType = useSelector(selectPlanType)
  const hasSubscription = useSelector(selectHasActiveOrTrialingEnterpriseSubscription)
  const enterprise = useSelector(selectCurrentEnterprise)
  const { trackEvent } = useAnalytics()
  const { locale, localizeNumber } = usePricing()

  // Get cached data from Redux
  const cachedUsers = useSelector(selectUsageUsers)
  const isUsageDataLoaded = useSelector(selectUsageIsLoaded)
  const lastFetchedAt = useSelector(selectUsageLastFetchedAt)

  // Determine access levels
  const canViewTeammateMetrics = isAdmin && hasSubscription && planType === PlanType.Enterprise
  const isLeaderboardHidden = !isAdmin && enterprise?.settings?.[SettingType.hideLeaderboard] === true
  const canViewLeaderboard = hasSubscription && planType === PlanType.Enterprise && !isLeaderboardHidden
  const canViewAggregateMetrics = isAdmin // Admins can always see aggregate metrics
  const canExportUsage = canViewTeammateMetrics && enterprise?.settings?.[SettingType.usageExportAllowed] !== false
  const shouldFetchUsageData = canViewAggregateMetrics || canViewLeaderboard

  // Only read cached usage data for roles that are allowed to use it on this page.
  const members = shouldFetchUsageData ? cachedUsers : []

  // Cache expiration: 3 hours in milliseconds
  const CACHE_DURATION_MS = 3 * 60 * 60 * 1000

  useEffect(() => {
    const fetchEnterpriseUsage = async () => {
      if (!shouldFetchUsageData) {
        setLoading(false)
        return
      }

      // Check if cache is still valid (less than 3 hours old)
      const now = Date.now()
      const cacheAge = lastFetchedAt ? now - lastFetchedAt : Infinity
      const isCacheValid = isUsageDataLoaded && cacheAge < CACHE_DURATION_MS

      // If we have valid cached data, use it
      if (isCacheValid) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Fetch all enterprise users with their stats in a single call
        const response = await getEnterpriseUsage()

        // Store data in Redux
        dispatch(
          setUsageData({
            users: response.data.users,
          })
        )
      } catch (err) {
        console.error('Failed to fetch enterprise usage:', err)
        setError('Failed to load usage data')
      } finally {
        setLoading(false)
      }
    }

    fetchEnterpriseUsage()
  }, [shouldFetchUsageData, isUsageDataLoaded, lastFetchedAt, cachedUsers.length, dispatch, CACHE_DURATION_MS])

  const isWaitingOnUsageData = shouldFetchUsageData && !isUsageDataLoaded && !error

  if (loading || isWaitingOnUsageData) {
    return (
      <>
        <div className={`${styles.container} ${styles.usageContainer}`}>
          <div className={styles.loadingState}>Loading usage...</div>
        </div>
      </>
    )
  }

  // Only show error if we have an actual error, or if data was loaded but is empty
  // Don't show error if we haven't loaded data yet (isUsageDataLoaded will be false initially)
  if (error || (isUsageDataLoaded && members.length === 0 && canViewAggregateMetrics)) {
    return (
      <>
        <div className={`${styles.container} ${styles.usageContainer}`}>
          <div className={styles.errorState}>{error || 'No data available'}</div>
        </div>
      </>
    )
  }

  // Only block non-admins from seeing the entire page
  const blockingBannerMessage = !isAdmin ? 'You must be an admin to view team usage metrics' : ''

  // Banner message for the teammate table section when user doesn't have Enterprise plan
  // Show same message for everyone without Enterprise plan (no subscription, Team plan, Pro plan, etc.)
  const individualMetricsBannerMessage =
    isAdmin && !canViewTeammateMetrics ? 'Upgrade to Enterprise plan to view individual teammate metrics' : ''

  const displayName = userName || 'User'
  const firstName = displayName.split(' ')[0]

  // --- Overview-only computations (skip when leaderboard is active) ---
  let totalWords = 0
  let wordsDictatedThisWeek = 0
  let wordsPerWeekData: ChartDataPoint[] = []
  let trend: TrendData = { percentage: 0, direction: 'neutral', isPositive: false }
  let dateRange = ''
  let canGoBack = false
  let canGoForward = false
  let globalMaxWords = 0
  let totalTeamMembers = 0
  let teammateData: TeammateRow[] = []

  if (activeTab === 0) {
    // Filter members based on user selection
    const filteredMembers = selectedUserId === 'all' ? members : members.filter((m) => m.user_id === selectedUserId)

    // Aggregate stats across filtered members
    const aggregatedStats = filteredMembers.reduce(
      (acc, member) => {
        if (!member.stats) return acc
        return { total_words: acc.total_words + member.stats.total_words }
      },
      { total_words: 0 }
    )

    // Compute all data upfront to determine global max for Y-axis scaling
    const allPossibleWeeks = computeAllPossibleWeeks(filteredMembers)
    const weeklyWordCounts = computeWeeklyWordCounts(allPossibleWeeks, filteredMembers)
    const wordsThisWeek = weeklyWordCounts.length > 0 ? weeklyWordCounts[0].wordcount : 0
    const allPossibleDays = computeAllPossibleDays(filteredMembers)
    const dailyWordCounts = computeDailyWordCounts(allPossibleDays, filteredMembers)

    // Calculate global max for consistent Y-axis across all pages
    globalMaxWords =
      viewMode === 'weekly'
        ? Math.max(...weeklyWordCounts.map((w) => w.wordcount), 0)
        : Math.max(...dailyWordCounts.map((d) => d.wordcount), 0)

    // Compute data based on view mode
    let chartData: ChartDataPoint[] = []

    if (viewMode === 'weekly') {
      const WEEKS_PER_PAGE = 10
      const visibleWeeklyData = weeklyWordCounts.slice(weekOffset, weekOffset + WEEKS_PER_PAGE)
      canGoBack = weekOffset + WEEKS_PER_PAGE < weeklyWordCounts.length
      canGoForward = weekOffset > 0

      chartData = visibleWeeklyData
        .map((week) => ({
          month: week.week ? formatWeekToDate(week.week, locale) : 'Unknown',
          words: week.wordcount,
        }))
        .reverse()

      trend = calculateTrend(visibleWeeklyData)
      dateRange = getDateRange(visibleWeeklyData)
    } else {
      const DAYS_PER_PAGE = 14
      const visibleDailyData = dailyWordCounts.slice(dayOffset, dayOffset + DAYS_PER_PAGE)
      canGoBack = dayOffset + DAYS_PER_PAGE < dailyWordCounts.length
      canGoForward = dayOffset > 0

      chartData = visibleDailyData
        .map((day) => ({
          month: day.day ? formatDayToDate(day.day, locale) : 'Unknown',
          words: day.wordcount,
        }))
        .reverse()

      trend = calculateTrendGeneric(visibleDailyData)
      dateRange = getDateRangeGeneric(visibleDailyData, (day) => formatDayToDate(day, locale))
    }

    wordsPerWeekData = chartData
    totalWords = aggregatedStats.total_words
    wordsDictatedThisWeek = wordsThisWeek

    // Build teammate data from members
    teammateData = members
      .filter((member) => member.stats)
      .map((member) => ({
        userId: member.user_id,
        name: `${member.first_name} ${member.last_name}`,
        email: member.email,
        words: member.stats?.total_words || 0,
        lastUsed: formatLastUsed(member.stats?.last_transcript_timestamp || null, locale),
        lastUsedTimestamp: member.stats?.last_transcript_timestamp
          ? new Date(member.stats.last_transcript_timestamp).getTime()
          : 0,
        isYou: member.user_id === currentUser.userId,
        avatar: member.first_name?.charAt(0).toUpperCase() || '?',
        color: '#8B4755',
      }))
      .sort((a, b) => {
        let comparison = 0

        switch (sortField) {
          case 'name':
            comparison = a.name.localeCompare(b.name)
            break
          case 'words':
            comparison = a.words - b.words
            break
          case 'lastUsed':
            comparison = a.lastUsedTimestamp - b.lastUsedTimestamp
            break
        }

        return sortDirection === 'asc' ? comparison : -comparison
      })

    totalTeamMembers = teammateData.length
  }

  // Pagination navigation handlers (only used on Overview tab)
  const handleGoBack = () => {
    const filtered = selectedUserId === 'all' ? members : members.filter((m) => m.user_id === selectedUserId)
    if (viewMode === 'weekly') {
      const WEEKS_PER_PAGE = 10
      const allPossibleWeeks = computeAllPossibleWeeks(filtered)
      const weeklyWordCounts = computeWeeklyWordCounts(allPossibleWeeks, filtered)
      setWeekOffset((prev) => Math.min(prev + WEEKS_PER_PAGE, weeklyWordCounts.length - WEEKS_PER_PAGE))
    } else {
      const DAYS_PER_PAGE = 14
      const allPossibleDays = computeAllPossibleDays(filtered)
      const dailyWordCounts = computeDailyWordCounts(allPossibleDays, filtered)
      setDayOffset((prev) => Math.min(prev + DAYS_PER_PAGE, dailyWordCounts.length - DAYS_PER_PAGE))
    }
  }

  const handleGoForward = () => {
    if (viewMode === 'weekly') {
      setWeekOffset((prev) => Math.max(prev - 10, 0))
    } else {
      setDayOffset((prev) => Math.max(prev - 14, 0))
    }
  }

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode)
    // Reset offsets when switching modes so the chart starts at the most recent data
    setWeekOffset(0)
    setDayOffset(0)
  }

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // Default to descending for new field (except name, which defaults to ascending)
      setSortField(field)
      setSortDirection(field === 'name' ? 'asc' : 'desc')
    }
  }

  return (
    <>
      <div className={`${styles.container} ${styles.usageContainer}`}>
        <div className={styles.header}>
          <p className={styles.welcomeText}>Welcome, {firstName}</p>
          {/* Only show user filter for Enterprise plan (allows individual user drill-down) */}
          {canViewTeammateMetrics && activeTab === 0 && (
            <div className={styles.filterContainer}>
              <label>View by User:</label>
              <select
                className={styles.userFilter}
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="all">All Users</option>
                {[...members]
                  .sort((a, b) => {
                    const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email
                    const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.email
                    return nameA.localeCompare(nameB)
                  })
                  .map((member) => {
                    const displayName =
                      member.first_name || member.last_name
                        ? `${member.first_name || ''}${member.last_name ? ' ' + member.last_name : ''}`.trim()
                        : member.email
                    return (
                      <option key={member.user_id} value={member.user_id}>
                        {displayName}
                        {member.user_id === currentUser.userId ? ' (You)' : ''}
                      </option>
                    )
                  })}
              </select>
            </div>
          )}
        </div>

        {/* Tab Strip - visible to all Enterprise users */}
        {canViewLeaderboard && (
          <Tabs tabs={['Overview', 'Leaderboard']} activeTab={activeTab} setActiveTab={setActiveTab} />
        )}

        {activeTab === 0 && (
          <div style={{ position: 'relative' }}>
            {blockingBannerMessage && (
              <>
                <div className={styles.sectionBanner}>
                  <div>{blockingBannerMessage}</div>
                </div>
                <div className={styles.sectionBlurOverlay} />
              </>
            )}
            {/* Metrics Cards */}
            <div className={styles.metricsGrid}>
              <div className={styles.metricCard}>
                <div className={styles.metricIcon}>
                  <TeamIcon />
                </div>
                <div className={styles.metricContent}>
                  <div className={styles.metricValue}>{totalTeamMembers}</div>
                  <div className={styles.metricLabel}>Team Members</div>
                </div>
              </div>
              <div className={styles.metricCard}>
                <div className={styles.metricIcon}>
                  <MicrophoneIcon />
                </div>
                <div className={styles.metricContent}>
                  <div className={styles.metricValue}>{localizeNumber(totalWords)}</div>
                  <div className={styles.metricLabel}>Total Words</div>
                </div>
              </div>
              <div className={styles.metricCard}>
                <div className={styles.metricIcon}>
                  <DocumentIcon />
                </div>
                <div className={styles.metricContent}>
                  <div className={styles.metricValue}>{localizeNumber(wordsDictatedThisWeek)}</div>
                  <div className={styles.metricLabel}>Words this week</div>
                </div>
              </div>
            </div>

            {/* Charts Row */}
            <div className={styles.chartsGrid}>
              {/* Words per week chart */}
              <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                  <div className={styles.viewToggle}>
                    <button
                      className={`${styles.toggleButton} ${viewMode === 'daily' ? styles.active : ''}`}
                      onClick={() => handleViewModeChange('daily')}
                    >
                      Daily
                    </button>
                    <button
                      className={`${styles.toggleButton} ${viewMode === 'weekly' ? styles.active : ''}`}
                      onClick={() => handleViewModeChange('weekly')}
                    >
                      Weekly
                    </button>
                  </div>
                  <div className={styles.chartTitleWrapper}>
                    <p>Words per {viewMode === 'weekly' ? 'week' : 'day'}</p>
                  </div>
                  {(canGoBack || canGoForward) && (
                    <div className={styles.chartNavigation}>
                      <button
                        className={styles.navButton}
                        onClick={handleGoBack}
                        disabled={!canGoBack}
                        aria-label={`View older ${viewMode === 'weekly' ? 'weeks' : 'days'}`}
                      >
                        ←
                      </button>
                      <button
                        className={styles.navButton}
                        onClick={handleGoForward}
                        disabled={!canGoForward}
                        aria-label={`View newer ${viewMode === 'weekly' ? 'weeks' : 'days'}`}
                      >
                        →
                      </button>
                    </div>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={wordsPerWeekData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#666', fontSize: 12 }} />
                    <YAxis hide domain={[0, globalMaxWords]} />
                    <Tooltip
                      cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                      contentStyle={{
                        background: '#fff',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                      formatter={(value) => localizeNumber(Number(value))}
                    />
                    <Bar dataKey="words" fill="#D4C5E2" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className={styles.chartFooter}>
                  {trend.percentage > 0 && (
                    <span className={styles.trendIndicator}>
                      <span className={styles.trendArrow}>{trend.direction === 'up' ? '↗' : '↘'}</span> Trending{' '}
                      {trend.direction} by {trend.percentage.toFixed(1)}% last week
                    </span>
                  )}
                  <span className={styles.dateRange}>{dateRange}</span>
                </div>
              </div>
            </div>

            {/* Teammate Metrics Table - Show for both Team and Enterprise, but blur for Team */}
            {canViewAggregateMetrics && (
              <>
                <div className={styles.tableTitleRow}>
                  <p className={styles.tableTitle}>Teammate Metrics</p>
                  {canExportUsage && (
                    <Button
                      type="dark"
                      onClick={() => {
                        setIsExporting(true)
                        // Defer to next frame so the spinner renders before the blocking CSV work
                        setTimeout(() => {
                          exportUsageCSV(members)
                          trackEvent?.(EventName.ExportUsageClicked, { member_count: members.length })
                          setIsExporting(false)
                        }, 0)
                      }}
                      isLoading={isExporting}
                      isDisabled={loading || isExporting}
                      title={
                        <>
                          <Download size={16} />
                          Export Usage
                        </>
                      }
                    />
                  )}
                </div>
                <div className={styles.tableCard} style={{ position: 'relative' }}>
                  {individualMetricsBannerMessage && (
                    <>
                      <div className={styles.sectionBanner}>
                        <div>{individualMetricsBannerMessage}</div>
                        <Button
                          title="Upgrade to Enterprise"
                          onClick={() => navigate('/app/settings/billing')}
                          type="dark"
                        />
                      </div>
                      <div className={styles.sectionBlurOverlay} />
                    </>
                  )}
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.sortableHeader} onClick={() => handleSort('name')}>
                          NAME
                          <span className={styles.sortIcon}>
                            {sortField === 'name' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        </th>
                        <th className={styles.sortableHeader} onClick={() => handleSort('words')}>
                          # OF WORDS
                          <span className={styles.sortIcon}>
                            {sortField === 'words' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        </th>
                        <th className={styles.sortableHeader} onClick={() => handleSort('lastUsed')}>
                          LAST USED AT
                          <span className={styles.sortIcon}>
                            {sortField === 'lastUsed' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {teammateData.map((member) => (
                        <tr key={member.userId}>
                          <td>
                            <div className={styles.memberInfo}>
                              <div className={styles.avatar} style={{ backgroundColor: member.color }}>
                                {member.avatar}
                              </div>
                              <div className={styles.memberDetails}>
                                <div className={styles.memberName}>
                                  {member.name}
                                  {member.isYou && <span className={styles.youBadge}>YOU</span>}
                                </div>
                                <div className={styles.memberEmail}>{member.email}</div>
                              </div>
                            </div>
                          </td>
                          <td>{localizeNumber(member.words)}</td>
                          <td className={styles.lastUsed}>{member.lastUsed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 1 && (
          <LeaderboardSection
            members={members}
            currentUserId={currentUser.userId ?? ''}
            canViewLeaderboard={canViewLeaderboard}
            canViewTeammateMetrics={canViewTeammateMetrics}
          />
        )}
      </div>
    </>
  )
}

export default UsagePage
